import { Button, Flex, Modal, type TableColumnsType, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { useQuery } from 'jsonapi-react';
import { CircleAlert, PlusCircle, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';
import { type Key, type ReactNode, useReducer, useState } from 'react';
import { from, tap } from 'rxjs';
import SupplyFilter from './supplies.filter';
import SupplyForm from './supply.form';
import AuthorizedComponent from '../../../../components/authorized-component';
import { DataTable } from '../../../../components/data-table';
import SyncSuspense from '../../../../components/sync-suspense';
import { ExtraContent } from '../../../../contexts/extra-content-context-provider';
import Supply from '../../../../entities/supply';
import useDebouncedState from '../../../../hooks/use-debounced-state';
import { useNotification } from '../../../../hooks/use-notification';
import { Roles } from '../../../../types';
import utils from '../../../../utils/utils';

export interface SupplyFiltersProps {
  onChange?: (filters: FiltersState) => void;
  initialFilters: FiltersState;
}

interface FiltersState {
  query: string;
}

interface SupplyPageState {
  filters: SupplyFiltersProps['initialFilters']; // TODO: To be replaced
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'ascend';
const SUCCESS_DELETE_MESSAGE = 'Supply deleted successfully';

const serializeSort = (sort: SupplyPageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};

const serializeFilters = (filters: SupplyPageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);

  return parts.length ? parts.join(';') : undefined;
};

const reducer = (
  state: SupplyPageState,
  action: { type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT'; payload?: any }
): SupplyPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: { query: '' } };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload,
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload,
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as SupplyPageState['sort'],
      };
  }
};
const SuppliesPage = (): ReactNode => {
  const [editedSupply, setEditedSupply] = useState<Supply | undefined>(undefined);
  const { token } = theme.useToken();
  const notification = useNotification();
  const [state, setState] = useReducer(reducer, {
    filters: { query: '' },
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const supplies = useQuery<Supply[]>(
    debouncedState && [
      'supply',
      {
        filter: serializeFilters(debouncedState.filters),
        page: {
          size: debouncedState.pagination.pageSize,
          number: debouncedState.pagination.current,
          totals: 1,
        },
        sort: serializeSort(debouncedState.sort),
      },
    ]
  );

  const columns: TableColumnsType<Supply> = [
    {
      title: 'Name',
      dataIndex: ['name'],
      sorter: true,
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            setEditedSupply(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
    {
      title: 'Description',
      dataIndex: ['description'],
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Price',
      dataIndex: ['price'],
      render: (_, record) => utils.currencyFormat(record.price),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
  ];

  const handleTableChange = (pagination: any, f: any, sorter: any): void => {
    setState({
      type: 'SET_PAGINATION',
      payload: { current: pagination.page, pageSize: pagination.size },
    });
    setState({ type: 'SET_SORT', payload: Array.isArray(sorter) ? sorter[0] : sorter });
  };
  return (
    <Flex gap="middle" vertical>
      <DataTable<Supply>
        title="Supplies"
        description="List of supplies customers can order"
        filtersNode={
          <SupplyFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        actionsNode={
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Button
              type="primary"
              icon={<PlusCircle />}
              onClick={() => {
                setEditedSupply(new Supply());
              }}
            >
              Add Supply
            </Button>
          </AuthorizedComponent>
        }
        dataList={supplies}
        onChange={setState}
        columns={columns}
        currentPage={state.pagination.current}
        actions={[
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 />,
            danger: true,
            onItemClick: (record) => {
              Modal.confirm({
                width: '50%',
                title: 'Confirm deletion',
                icon: <CircleAlert />,
                content: (
                  <Typography.Text>
                    Are you sure you want to delete <b>{record.name}</b> permanently?
                  </Typography.Text>
                ),
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  from(supplies.client.delete(['supply', record.id]))
                    .pipe(
                      tap((res) => {
                        if (res?.error) {
                          notification.error({
                            message: `Failed to delete ${record.name}`,
                            description: res.error.detail,
                          });
                        } else {
                          notification.success({
                            message: ' ',
                            description: SUCCESS_DELETE_MESSAGE,
                          });
                          supplies.refetch?.();
                        }
                      })
                    )
                    .subscribe();
                },
              });
            },
          },
        ]}
      />
      <SyncSuspense fallback="" condition={editedSupply}>
        <ExtraContent>
          <SupplyForm
            supply={editedSupply ?? new Supply()}
            onSuccess={() => {
              supplies.refetch?.();
              setEditedSupply(undefined);
            }}
            onCancel={() => {
              setEditedSupply(undefined);
            }}
          />
        </ExtraContent>
      </SyncSuspense>
    </Flex>
  );
};

SuppliesPage.propTypes = {
  defaultPageNumber: PropTypes.number,
  defaultPageSize: PropTypes.number,
};

SuppliesPage.defaultProps = {
  defaultPageNumber: 1,
  defaultPageSize: 10,
  onVisibleColumnsChange: () => {},
  onSelectRowsChange: () => {},
};

SuppliesPage.displayName = 'SuppliesPage';
export default SuppliesPage;
